// src/frontend/.../Recruitment/validation/validateBeforeNavigate.js
import errorTrackerV2 from "./Utils/ErrorTrackerV2";

// loaders
import { getDataS1 } from "../../../../../backend/Recruitment/Sections/Section1/readDataS1";
import { readDataS2 } from "../../../../../backend/Recruitment/Sections/Section2/readDataS2";
import readDataS3 from "../../../../../backend/Recruitment/Sections/Section3/readDataS3";
import { readDataS4 } from "../../../../../backend/Recruitment/Sections/Section4/readDataS4";
import { readDataS5 } from "../../../../../backend/Recruitment/Sections/Section5/readDataS5";
import { readDataS6 } from "../../../../../backend/Recruitment/Sections/Section6/readDataS6";
import { readDataS7 } from "../../../../../backend/Recruitment/Sections/Section7/readDataS7";
import { readDataS8 } from "../../../../../backend/Recruitment/Sections/Section8/readDataS8";
import { readDataS9 } from "../../../../../backend/Recruitment/Sections/Section9/readDataS9";
import { readDataS10 } from "../../../../../backend/Recruitment/Sections/Section10/readDataS10";
import { readDataS11 } from "../../../../../backend/Recruitment/Sections/Section11/readDataS11";

// validators (YOUR single source of truth)
import { validateSection1 } from "./section1";
import { validateSection2 } from "./section2";
import { validateSection3 } from "./section3";
import { validateSection4 } from "./section4";
import { validateSection5 } from "./section5";
import { validateSection6 } from "./section6";
import { validateSection7 } from "./section7";
import { validateSection8 } from "./section8";
import { validateSection9 } from "./section9";
import { validateSection10 } from "./section10";
import { validateSection11 } from "./section11";

// Wrapper functions for sections 9, 10, 11 that have different signatures
// Note: These sections call errorTrackerV2.setSectionErrors() internally,
// so we just need to trigger the validation and return empty object
// to prevent duplicate error setting in validateAllSections
const validateSection9Wrapper = (data) => {
  validateSection9({
    formData: data,
    setErrors: () => {}, // No-op since section sets errors internally
    setIsValid: () => {},
    onValidationStatusChange: () => {},
  });
  // Return empty to prevent double-setting errors
  return {};
};

const validateSection10Wrapper = (data) => {
  validateSection10({
    formData: data,
    setErrors: () => {}, // No-op since section sets errors internally
    onValidationStatusChange: () => {},
  });
  // Return empty to prevent double-setting errors
  return {};
};

const validateSection11Wrapper = (data) => {
  validateSection11({
    formData: data,
    setErrors: () => {}, // No-op since section sets errors internally
    onValidationStatusChange: () => {},
  });
  // Return empty to prevent double-setting errors
  return {};
};

const SECTION_RULES = {
  section1: { load: getDataS1, validate: validateSection1 },
  section2: { load: readDataS2, validate: validateSection2 },
  section3: { load: readDataS3, validate: validateSection3 },
  section4: { load: readDataS4, validate: validateSection4 },
  section5: { load: readDataS5, validate: validateSection5 },
  section6: { load: readDataS6, validate: validateSection6 },
  section7: { load: readDataS7, validate: validateSection7 },
  section8: { load: readDataS8, validate: validateSection8 },
  section9: { load: readDataS9, validate: validateSection9Wrapper },
  section10: { load: readDataS10, validate: validateSection10Wrapper },
  section11: { load: readDataS11, validate: validateSection11Wrapper },
};

/**
 * Validates all sections BEFORE targetStep.
 * Uses live current section data if provided, otherwise loads from Firestore.
 */
export async function validateBeforeNavigate(
  targetStep,
  { currentSectionKey = null, currentSectionData = null } = {},
) {
  const target = Math.max(1, Number(targetStep || 1));
  const last = target - 1;

  // Load and validate ALL sections in parallel for speed
  const validationPromises = [];

  for (let i = 1; i <= last; i++) {
    const key = `section${i}`;
    const rule = SECTION_RULES[key];
    if (!rule) continue;

    // Create a promise for this section's validation
    const validateSection = (async () => {
      try {
        // Use live data if it's the section currently being edited
        const data =
          currentSectionKey === key && currentSectionData
            ? currentSectionData
            : await rule.load();

        // Validators now set errors internally via errorTrackerV2.setSectionErrors()
        rule.validate(data || {});
      } catch (e) {
        // console.error(`Error validating ${key}:`, e);
        // errorTrackerV2.setSectionErrors(key, {
        //   _load: "Unable to validate this section. Please open and save it.",
        // });
      }
    })();

    validationPromises.push(validateSection);
  }

  // Wait for all validations to complete
  await Promise.all(validationPromises);
}

/**
 * Validate ALL sections (1-11) in parallel
 * Always fetches fresh data from Firestore (no cached data)
 * Used for initial load and comprehensive validation
 */
export async function validateAllSections() {
  const dataReceivedMap = {}; // Track actual Firebase responses
  const validationPromises = [];

  for (let i = 1; i <= 11; i++) {
    const key = `section${i}`;
    const rule = SECTION_RULES[key];
    if (!rule) continue;

    // Create a promise for this section's validation
    const validateSection = (async () => {
      try {
        // ALWAYS fetch fresh data from Firestore
        const data = await rule.load();

        // Track whether data was actually received
        dataReceivedMap[key] = data !== null && data !== undefined;

        // Validators now set errors internally via errorTrackerV2.setSectionErrors()
        rule.validate(data || {});
      } catch (e) {
        // console.error(`❌ Error validating ${key}:`, e);
        dataReceivedMap[key] = false; // Treat errors as no data received
        errorTrackerV2.setSectionErrors(key, {
          _load: "Unable to validate this section. Please open and save it.",
        });
      }
    })();

    validationPromises.push(validateSection);
  }

  // Wait for all validations to complete
  await Promise.all(validationPromises);

  // Force dataNotFound error for sections with no data received
  for (let i = 1; i <= 11; i++) {
    const key = `section${i}`;
    const dataReceived = dataReceivedMap[key] ?? false;

    if (!dataReceived) {
      // No data received from Firebase - set error
      errorTrackerV2.setSectionErrors(key, {
        dataNotFound:
          "No data found for this section. Please open and save it first.",
      });
    }
  }

  // Print summary table with actual Firebase data received
  const summary = [];
  const detailSummary = [];
  for (let i = 1; i <= 11; i++) {
    const key = `section${i}`;
    const state = errorTrackerV2.getSectionState(key);
    const dataReceived = dataReceivedMap[key] ?? false;
    summary.push({
      Section: i,
      Name: SECTIONS[i - 1]?.label || key,
      "Data Received": dataReceived ? "✅ Yes" : "❌ No",
      Errors: state.errorCount,
      Status: state.hasErrors ? "❌ INVALID" : "✅ VALID",
    });
    detailSummary.push({
      Section: i,
      HasErrors: state.hasErrors ? "❌" : "✅",
      ErrorCount: state.errorCount,
      ErrorDetails: JSON.stringify(state.errors || {}),
    });
  }

  // console.log("✅ [validateAllSections] All sections validated - Summary:");
  // console.table(summary);
  // console.log("🔍 [validateAllSections] Detailed Error Info:");
  // console.table(detailSummary);
}

const SECTIONS = [
  { label: "Applicant Details" },
  { label: "Professional Qualifications" },
  { label: "Activity" },
  { label: "Company Directorship Details" },
  { label: "Accountant Details" },
  { label: "Business Conduct" },
  { label: "Fitness and Propriety" },
  { label: "Overall Financial Position" },
  { label: "Additional Information" },
  { label: "Unemployment Details" },
  { label: "Declaration" },
];
